/* AddressAutocomplete — adresní našeptávač.
 *
 * Zdroj: nejdřív Mapy.com přes backend proxy /api/mapy/suggest (kvalitní CZ adresy + GPS,
 * když je v backend/.env MAPY_API_KEY); jinak fallback na OSM Nominatim (zdarma, bez klíče).
 * Debounce 350 ms; při výběru vrací { street, city, psc, region, lat?, lon? } přes onSelect.
 *
 * Usage:
 *   <AddressAutocomplete value={form.street} onChange={(v)=>...} onSelect={({street,city,psc,region})=>...} testid="detail-street" />
 */
import React, { useState, useRef, useEffect } from "react";
import { MapPin, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { adresyZeme, nominatimUrl } from "@/lib/adresyZeme";

export default function AddressAutocomplete({ value, onChange, onSelect, testid, placeholder }) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ""); }, [value]);

  // Nominatim výsledek → jednotný tvar { main, sub, street, city, psc, region }.
  const fromNominatim = (r) => {
    const a = r.address || {};
    const road = a.road || a.pedestrian || a.footway || "";
    const num = a.house_number ? ` ${a.house_number}` : "";
    const street = `${road}${num}`.trim() || (r.display_name?.split(",")[0] || "");
    const city = a.city || a.town || a.village || a.municipality || "";
    const psc = (a.postcode || "").replace(/\s/g, "");
    const region = a.state || a.region || a.county || "";
    // Nominatim vrací lat/lon na kořeni výsledku → převezmi je (jinak by GPS chybělo,
    // když Mapy.com proxy není/selže — potřebují je mapa klientů i cesta v diáři).
    const lat = r.lat != null ? parseFloat(r.lat) : undefined;
    const lon = r.lon != null ? parseFloat(r.lon) : undefined;
    return { main: street, sub: [city, psc, region].filter(Boolean).join(" · "), street, city, psc, region, lat, lon };
  };

  const fetchNominatim = (q) => {
    // Země a jazyk z TENANTA (SK poradce hledá na Slovensku), ne natvrdo ČR.
    return fetch(nominatimUrl(q), { headers: { "Accept-Language": adresyZeme().jazyk } })
      .then((r) => r.json())
      .then((data) => (Array.isArray(data) ? data.map(fromNominatim) : []))
      .catch(() => []);
  };

  const fetchSuggestions = async (q) => {
    if (!q || q.trim().length < 3) { setResults([]); setLoading(false); return; }
    setLoading(true);
    // 1) Mapy.com přes backend proxy (když je klíč v .env)
    try {
      const { data } = await api.get(`/api/mapy/suggest?q=${encodeURIComponent(q)}`);
      if (data?.enabled && Array.isArray(data.items) && data.items.length) {
        setResults(data.items.map((it) => ({
          main: it.street || it.label, sub: [it.city, it.psc, it.region].filter(Boolean).join(" · "),
          street: it.street, city: it.city, psc: it.psc, region: it.region, lat: it.lat, lon: it.lon,
        })));
        setLoading(false);
        return;
      }
    } catch { /* proxy nedostupné → fallback */ }
    // 2) fallback Nominatim (zdarma, bez klíče)
    setResults(await fetchNominatim(q));
    setLoading(false);
  };

  const onInputChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    onChange(v);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 350);
  };

  const pick = (r) => {
    const finalStreet = r.street || r.main || "";
    setQuery(finalStreet);
    onChange(finalStreet);
    setOpen(false);
    onSelect && onSelect({ street: finalStreet, city: r.city || "", psc: r.psc || "", region: r.region || "", lat: r.lat, lon: r.lon });
  };

  return (
    <div className="relative">
      <div
        className="flex items-center gap-2 rounded-md px-2.5 py-1.5"
        style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
      >
        <MapPin size={13} style={{ color: "var(--accent)" }} className="flex-shrink-0" />
        <input
          value={query}
          onChange={onInputChange}
          onFocus={() => query.length >= 3 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder={placeholder || "Jeseniova 56, Praha — adresa (3+ znaků, auto-vyplnění)"}
          data-testid={testid}
          className="w-full text-sm outline-none bg-transparent"
          style={{ color: "var(--ink)" }}
        />
        {loading && <Loader2 size={11} className="animate-spin flex-shrink-0" style={{ color: "var(--accent)" }} />}
      </div>

      {open && results.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 rounded-lg z-50 max-h-64 overflow-y-auto"
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border)",
            boxShadow: "var(--elite-shadow-strong, 0 10px 40px rgba(80,50,30,0.18))",
          }}
          data-testid={`${testid || "addr"}-results`}
        >
          {results.map((r, i) => (
            <button
              key={i}
              onMouseDown={(e) => { e.preventDefault(); pick(r); }}
              className="w-full text-left px-3 py-2 transition-colors hover:bg-[#FFF3EA]"
              style={{ borderBottom: i < results.length - 1 ? "1px solid var(--border)" : "none" }}
            >
              <div className="flex items-start gap-2">
                <MapPin size={11} className="mt-0.5 flex-shrink-0" style={{ color: "var(--accent)" }} />
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-medium truncate" style={{ color: "var(--ink)" }}>{r.main}</div>
                  {r.sub && <div className="text-drobny mt-0.5 truncate" style={{ color: "var(--ink-muted)" }}>{r.sub}</div>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
